import React, { useState, useRef, useEffect, useMemo } from 'react';
import { stagesWithoutCompletion } from '../finalInspectionCheck';
import { useQuery } from '@tanstack/react-query';
import { StageTable } from './StageTable';
import { inspectionService } from '../../../services/inspectionService';
import { AustinBmpDetails } from './AustinBmpDetails';
import { LITE_HEADER_HEIGHT_VAR } from '../../../hooks/usePublishedHeight';
import type { BmpConditions, BmpTap } from '../../../services/bmpLogService';
import {
  FALLBACK_NOTES_CAPACITY,
  clampNoteToCapacity,
  countNoteLines,
  fitsNoteCapacity,
  governorLabel,
} from '../notesGovernor';
import { sameIssueChoices } from '../sameIssue';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CloudRain,
  Copy,
  FileText,
  Info,
  Loader2,
  Plus,
  Trash2,
  Undo2,
} from 'lucide-react';
import type { InspectionCategory } from '../../../utils/schemaQuestionMapper';
import { isQuestionVisible } from '../../../utils/schemaQuestionMapper';
import { api } from '../../../services/api';
import type { CorrectiveActionItem } from '../LiteInspectionFlowPage';
import { captureUnsavedCorrectiveActions } from '../correctiveActionCapture';
import {
  moveRow,
  rowsWithoutLocation,
  sortByLot,
} from '../correctiveActionOrder';
import { useSwipeUp } from '@/hooks/useSwipeUp';
import {
  InspectionDateField,
  InspectionTimeField,
} from './InspectionDateField';

type InspectionResponse = {
  questionId: string;
  value: any;
  photos?: string[];
  notes?: string;
  location?: string;
  requiresCorrection?: boolean;
  correctionDeadline?: string;
  correctiveActions?: CorrectiveActionItem[];
  // JIM-1008-06: this No is the same problem as another No in this visit.
  sameAsQuestionId?: string;
  timestamp: string;
};

type OpenCA = {
  findingId: string;
  notes: string;
  location: string;
  date: string;
};

type StageDateEntry = {
  start: string;
  completion: string;
  extractedStageId?: string;
};
type StageItem = {
  id: string;
  stage_name: string;
  stage_order: number;
  project_type: string;
};

type OpenAction = {
  id: string;
  created_by_inspection_id?: string | null;
  question_id: string;
  question_text: string;
  category: string;
  status: string;
  last_answer: boolean;
  last_condition: string;
  notes: string | null;
  location: string | null;
  priority: 'medium' | 'high' | null;
  deadline: string | null;
};

type QuestionsPhaseProps = {
  siteId: string;
  /** JIM-0919-09: site is flagged TX-Austin — show the BMP details editor. */
  austinDetails?: boolean;
  siteName: string;
  inspectionCategories: InspectionCategory[];
  responses: Record<string, InspectionResponse>;
  setResponses: React.Dispatch<
    React.SetStateAction<Record<string, InspectionResponse>>
  >;
  lastAnswers?: Record<
    string,
    {
      value: string | null;
      notes: string | null;
      findings: Array<{
        description: string;
        location: string | null;
        condition: string;
        is_resolved: boolean;
      }>;
    }
  >;
  openCorrectiveActions: Record<string, OpenCA[]>;
  setOpenCorrectiveActions: React.Dispatch<
    React.SetStateAction<Record<string, OpenCA[]>>
  >;
  correctiveActionsData: { corrective_actions: Array<OpenAction> } | undefined;
  verifiedCaIds: Record<string, string>;
  setVerifiedCaIds: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  answeredQuestions: number;
  totalQuestions: number;
  scrollToQuestionId?: string | null;
  // Error from the parent's submit path (network, server, schema drift).
  // Rendered in the footer next to this component's own validation error.
  serverError?: string | null;
  onSubmit: () => void;
  onBack: () => void;
  onShowSiteInfo?: () => void;
  // Stage props
  siteLoading: boolean;
  allStages: StageItem[];
  currentStage: { id: string; stage_name: string } | null | undefined;
  stageDates: Record<string, StageDateEntry>;
  setStageDates: React.Dispatch<
    React.SetStateAction<Record<string, StageDateEntry>>
  >;
  handleStageDateSetAndSave: (
    stageName: string,
    field: 'start' | 'completion',
    date: string
  ) => void;
  handleStageDateBlur: (
    stageName: string,
    field: 'start' | 'completion'
  ) => void;
  // CA review props
  reviewOpenActions: OpenAction[];
  caPriorities: Record<string, 'medium' | 'high' | null>;
  setCaPriorities: React.Dispatch<
    React.SetStateAction<Record<string, 'medium' | 'high' | null>>
  >;
  // EP-TRANSITION: legacy-carried CAs with no schema question (loose)
  looseCAs: OpenAction[];
  onReparentLooseCa: (caId: string, questionId: string) => Promise<void>;
  onVerifyLooseCa: (caId: string) => Promise<void>;
  // F-Comment: universal Inspection Report Comment surface (flows to page 1
  // of the generated PDF under the "Comment" section).
  reportComment: string;
  setReportComment: (value: string) => void;
  // JIM-0929-01/02: City of Austin notes + this visit's BMP taps.
  austinNotes?: string;
  setAustinNotes?: (value: string) => void;
  austinTaps?: BmpTap[];
  setAustinTaps?: (taps: BmpTap[]) => void;
  austinConditions?: BmpConditions;
  setAustinConditions?: (conditions: BmpConditions) => void;
  /** The filed inspection being edited, if any (its events are already logged). */
  austinFiledInspectionId?: string | null;
  // Inspection details (Jim 2026-08-20): editable conducted date (defaults
  // to today, back-dating allowed) + final-inspection declaration.
  conductedDate: string;
  setConductedDate: (value: string) => void;
  // JIM-0919-04: HH:MM next to the date; defaults to when the inspection
  // was opened, editable for back-dated visits.
  conductedTime: string;
  setConductedTime: (value: string) => void;
  isFinalInspection: boolean;
  setIsFinalInspection: (value: boolean) => void;
  // JIM-1027-11a: why it is final — the consultant sees it on the confirm page.
  finalInspectionNote?: string;
  setFinalInspectionNote?: (value: string) => void;
  alertConsultant?: boolean;
  setAlertConsultant?: (value: boolean) => void;
  clientName?: string | null;
  // #265: when the site requires rain inspections, let the inspector mark that
  // this visit also covers a required rain event (sends type "both").
  showRainEventToggle?: boolean;
  coversRainEvent?: boolean;
  setCoversRainEvent?: (value: boolean) => void;
  /** Opened from the Rain Event box: this visit is the rain event inspection. */
  rainEventStart?: boolean;
};

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export { StageDateCell } from './StageDateCell';

export function QuestionsPhase({
  siteId,
  austinDetails = false,
  siteName,
  inspectionCategories,
  responses,
  setResponses,
  lastAnswers,
  openCorrectiveActions,
  setOpenCorrectiveActions,
  correctiveActionsData,
  verifiedCaIds,
  setVerifiedCaIds,
  answeredQuestions,
  totalQuestions,
  scrollToQuestionId,
  serverError,
  onSubmit,
  onBack,
  onShowSiteInfo,
  // Stages
  siteLoading,
  allStages,
  currentStage,
  stageDates,
  setStageDates,
  handleStageDateSetAndSave,
  handleStageDateBlur,
  // F-Comment
  reportComment,
  setReportComment,
  // JIM-0929-01/02
  austinNotes = '',
  setAustinNotes = () => {},
  austinTaps = [],
  setAustinTaps = () => {},
  austinConditions = {},
  setAustinConditions = () => {},
  austinFiledInspectionId = null,
  // Inspection details
  conductedDate,
  setConductedDate,
  conductedTime,
  setConductedTime,
  isFinalInspection,
  setIsFinalInspection,
  finalInspectionNote = '',
  setFinalInspectionNote,
  alertConsultant,
  setAlertConsultant,
  clientName,
  // #265 dual-count
  showRainEventToggle,
  coversRainEvent,
  setCoversRainEvent,
  rainEventStart = false,
  // EP-TRANSITION loose CAs
  looseCAs,
  onReparentLooseCa,
  onVerifyLooseCa,
}: QuestionsPhaseProps) {
  const [expandedCaForms, setExpandedCaForms] = useState<Set<string>>(
    new Set()
  );
  // The answer Verify auto-flipped to Yes, per question, until Undo.
  const preVerifyAnswers = useRef<
    Record<string, InspectionResponse | undefined>
  >({});
  const [expandedCaGroups, setExpandedCaGroups] = useState<Set<string>>(
    new Set()
  );
  const [caFormState, setCaFormState] = useState<
    Record<
      string,
      {
        notes: string;
        location: string;
        actions: CorrectiveActionItem[];
        /** JIM-1030-15: the inspector reordered by hand; stop auto-sorting. */
        manualOrder?: boolean;
      }
    >
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // JIM-1008-06: every question in this visit, for "same issue as" choices.
  const allQuestionsFlat = useMemo(
    () =>
      inspectionCategories.flatMap((c) =>
        c.questions.map((q) => ({ id: q.id, question: q.question }))
      ),
    [inspectionCategories]
  );
  // Point a No at the question that carries the corrective action for both.
  const linkSameIssue = (questionId: string, targetId: string | null) => {
    setSubmitError(null);
    setResponses((prev) => {
      const cur = prev[questionId];
      if (!cur) return prev;
      const next = { ...cur, timestamp: new Date().toISOString() };
      if (targetId) {
        next.sameAsQuestionId = targetId;
        next.correctiveActions = [];
        next.requiresCorrection = false;
      } else {
        delete next.sameAsQuestionId;
      }
      return { ...prev, [questionId]: next };
    });
    if (targetId) {
      setCaFormState((prev) => {
        const n = { ...prev };
        delete n[questionId];
        return n;
      });
      setExpandedCaForms((prev) => {
        const n = new Set(prev);
        n.delete(questionId);
        return n;
      });
    } else {
      setExpandedCaForms((p) => new Set([...p, questionId]));
    }
  };

  // JIM-0919-03: the page-one notes "governor". The API measures how many
  // lines page 1 has left after this site's stage table; the box holds that
  // many lines and stops there, so notes never spill to page 2 and the box on
  // screen holds what the box on paper holds (Jim 2026-09-19).
  const { data: notesCapacityData } = useQuery({
    queryKey: ['notes-capacity', allStages.length, isFinalInspection],
    queryFn: () =>
      inspectionService.getNotesCapacity(allStages.length, isFinalInspection),
    enabled: !siteLoading,
    staleTime: 60 * 60 * 1000,
  });
  const notesCapacity = notesCapacityData ?? FALLBACK_NOTES_CAPACITY;
  const notesRows = Math.min(Math.max(notesCapacity.lines, 4), 18);
  const atNotesLimit =
    countNoteLines(reportComment, notesCapacity.chars_per_line) >=
    notesCapacity.lines;
  useEffect(() => {
    // The box can shrink after the text is in (final-inspection line, a
    // schema with more stages): keep what still fits.
    if (!fitsNoteCapacity(reportComment, notesCapacity)) {
      setReportComment(clampNoteToCapacity(reportComment, notesCapacity));
    }
  }, [notesCapacity, reportComment, setReportComment]);
  // Shane (pilot, 2026-08-20): caFormState dies when this component unmounts
  // (Generate Report -> report phase), but `responses` survives in the parent.
  // Coming Back to Draft, the Edit button expanded an EMPTY form — committed
  // CAs vanished from view, and the next Add/Done overwrote them locally.
  // Rehydrate the editor from the committed CAs on mount (also covers
  // draft-resume, which restores `responses` but never seeded the editor).
  useEffect(() => {
    setCaFormState((prev) => {
      const next = { ...prev };
      for (const [qid, r] of Object.entries(responses)) {
        if (!next[qid] && (r.correctiveActions?.length ?? 0) > 0) {
          next[qid] = {
            notes: '',
            location: '',
            actions: r.correctiveActions!,
          };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (scrollToQuestionId && questionRefs.current[scrollToQuestionId]) {
      questionRefs.current[scrollToQuestionId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [scrollToQuestionId]);

  // Any "No" response with no backing CA (no committed correctiveActions and
  // no open CA from prior inspections) is orphaned — the report would claim a
  // finding exists but the log would be empty. Auto-open the CA form so the
  // user is forced to either add one or change the answer. Skip when table
  // CAs exist for the question: those flow through the Verify/Undo path, and
  // a second No click is what explicitly opens the entry form.
  useEffect(() => {
    const orphaned: string[] = [];
    for (const cat of inspectionCategories) {
      for (const q of cat.questions) {
        const resp = responses[q.id];
        if (resp?.value !== false) continue;
        if ((resp.correctiveActions?.length || 0) > 0) continue;
        if ((openCorrectiveActions[q.id]?.length || 0) > 0) continue;
        const tableCAsForQ = (
          correctiveActionsData?.corrective_actions ?? []
        ).filter((ca) => ca.question_id === q.id && ca.status === 'open');
        if (tableCAsForQ.length > 0) continue;
        if (expandedCaForms.has(q.id)) continue;
        // Don't re-open right after Done: setResponses and setExpandedCaForms
        // dispatch together but responses state can land a tick late, which
        // made Done-with-3+-CAs re-open the form. caFormState.actions is the
        // authoritative pre-flush signal that CAs were just committed.
        if ((caFormState[q.id]?.actions?.length || 0) > 0) continue;
        orphaned.push(q.id);
      }
    }
    if (orphaned.length > 0) {
      setExpandedCaForms((prev) => {
        const n = new Set(prev);
        for (const id of orphaned) n.add(id);
        return n;
      });
      setCaFormState((prev) => {
        const next = { ...prev };
        for (const id of orphaned) {
          if (!next[id]) next[id] = { notes: '', location: '', actions: [] };
        }
        return next;
      });
    }
  }, [
    inspectionCategories,
    responses,
    openCorrectiveActions,
    correctiveActionsData,
    expandedCaForms,
    caFormState,
  ]);

  // --- Answer handlers ---
  const handleAnswer = async (questionId: string, value: any) => {
    setSubmitError(null);
    const prevResponse = responses[questionId];
    const isNoAnswer = value === false;
    const isYesAnswer = value === true;
    const needsCorrection = isNoAnswer;

    const openActions = openCorrectiveActions[questionId] ?? [];
    const tableCAsForQ = (
      correctiveActionsData?.corrective_actions ?? []
    ).filter((ca) => ca.question_id === questionId && ca.status === 'open');
    const unverifiedTableCAs = tableCAsForQ.filter(
      (ca) => !verifiedCaIds[ca.id]
    );

    // Yes is blocked while unresolved CAs remain. Locally-verified table CAs
    // are fine — the backend /resolve call already fired; the user can still
    // Undo, which puts that CA back in the unverified bucket.
    if (
      isYesAnswer &&
      (openActions.length > 0 || unverifiedTableCAs.length > 0)
    ) {
      setSubmitError(
        'Verify or resolve the open corrective actions before marking this question Yes.'
      );
      return;
    }

    // Yes after the inspector already committed CAs in this session: silently
    // wiping them produced ghost-CA desyncs (Jim 2026-04-30, Chris Test Site
    // inspection 79b7f816) — the form re-opened on No-toggle showing
    // form.actions while responses.correctiveActions was empty, so submit
    // failed with "needs a corrective action" despite a visible CA. Confirm
    // the discard, then clear caFormState so the ghost can't reappear.
    const committedCount = prevResponse?.correctiveActions?.length ?? 0;
    const draftActions = caFormState[questionId]?.actions?.length ?? 0;
    if (isYesAnswer && (committedCount > 0 || draftActions > 0)) {
      const ok = window.confirm(
        'You added a corrective action for this question. Mark it Yes and discard the CA?'
      );
      if (!ok) return;
      setCaFormState((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setExpandedCaForms((prev) => {
        const n = new Set(prev);
        n.delete(questionId);
        return n;
      });
    }

    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        questionId,
        value,
        requiresCorrection: needsCorrection,
        // Re-answering "No" must not wipe CAs committed this session — the
        // Yes path above confirms their discard explicitly.
        ...(!isYesAnswer && prev[questionId]?.correctiveActions?.length
          ? {
              correctiveActions: prev[questionId].correctiveActions,
              notes: prev[questionId].notes,
              location: prev[questionId].location,
            }
          : {}),
        timestamp: new Date().toISOString(),
      },
    }));

    if (isYesAnswer && openActions.length > 0) {
      try {
        // Resolve every open finding for this question. Backend sibling-
        // resolution (7f20877) covers this at the source_checklist_question_id
        // level, but resolving each explicitly protects against manual findings
        // that don't share a source_checklist_question_id.
        await Promise.all(
          openActions.map((oa) =>
            api.patch(`/sites/${siteId}/findings/${oa.findingId}/resolve`, {
              resolution_notes:
                'Resolved - confirmed satisfactory during inspection',
            })
          )
        );
        setOpenCorrectiveActions((prev) => {
          const u = { ...prev };
          delete u[questionId];
          return u;
        });
      } catch (error) {
        console.error('Failed to auto-resolve corrective action:', error);
      }
    }

    if (needsCorrection) {
      // Clicking No always opens the corrective-action input directly.
      // Existing open CAs (from prior inspections) remain visible inside the
      // form and can be verified/resolved individually; adding a new CA is
      // done via the "Add" button inside the form.
      setExpandedCaForms((prev) => new Set([...prev, questionId]));
      setCaFormState((prev) => ({
        ...prev,
        [questionId]: prev[questionId] || {
          notes: '',
          location: '',
          actions: responses[questionId]?.correctiveActions || [],
        },
      }));
    } else {
      setExpandedCaForms((prev) => {
        const n = new Set(prev);
        n.delete(questionId);
        return n;
      });
    }
  };

  const handleAddCa = (questionId: string) => {
    const form = caFormState[questionId];
    const notes = form?.notes?.trim() || '';
    const location = form?.location?.trim() || '';
    // Both are required: the issue text (JIM-1015-10 — a lot number alone
    // printed as "[A:1]") and the location (JIM-1030-15, Jim 10/30: a vague
    // "entire site" action cannot be billed or found). The Add button stays
    // off until both are there.
    if (!notes || !location) return;
    setSubmitError(null);
    const newAction: CorrectiveActionItem = {
      id: generateId(),
      location,
      notes,
    };
    // Merge against the committed truth: after a remount the form can be
    // empty while responses still hold this session's CAs (Shane 2026-08-20).
    const base = form?.actions?.length
      ? form.actions
      : (responses[questionId]?.correctiveActions ?? []);
    // The list runs by lot number unless the inspector has reordered it.
    const updated = form?.manualOrder
      ? [...base, newAction]
      : sortByLot([...base, newAction]);
    setCaFormState((prev) => ({
      ...prev,
      [questionId]: {
        notes: '',
        location: '',
        actions: updated,
        manualOrder: form?.manualOrder,
      },
    }));
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        correctiveActions: updated,
        notes: updated[0]?.notes,
        location: updated[0]?.location,
      },
    }));
  };

  const handleUpdateCaLocation = (
    questionId: string,
    actionId: string,
    location: string
  ) => {
    setCaFormState((prev) => {
      const f = prev[questionId];
      if (!f) return prev;
      return {
        ...prev,
        [questionId]: {
          ...f,
          actions: f.actions.map((a) =>
            a.id === actionId ? { ...a, location } : a
          ),
        },
      };
    });
    setResponses((prev) => {
      const e = prev[questionId];
      if (!e) return prev;
      return {
        ...prev,
        [questionId]: {
          ...e,
          correctiveActions: (e.correctiveActions || []).map((a) =>
            a.id === actionId ? { ...a, location } : a
          ),
          location:
            (e.correctiveActions || []).find((a) => a.id === actionId) &&
            e.correctiveActions?.[0]?.id === actionId
              ? location
              : e.location,
        },
      };
    });
  };

  // JIM-1030-15: once a location is typed the row finds its place by lot,
  // unless the inspector has ordered the list by hand.
  const syncCaOrder = (questionId: string, actions: CorrectiveActionItem[]) => {
    setCaFormState((prev) => {
      const f = prev[questionId];
      return f ? { ...prev, [questionId]: { ...f, actions } } : prev;
    });
    setResponses((prev) => {
      const e = prev[questionId];
      if (!e) return prev;
      return {
        ...prev,
        [questionId]: {
          ...e,
          correctiveActions: actions,
          notes: actions[0]?.notes,
          location: actions[0]?.location,
        },
      };
    });
  };
  const handleResortCa = (questionId: string) => {
    const form = caFormState[questionId];
    if (!form || form.manualOrder || form.actions.length < 2) return;
    const sorted = sortByLot(form.actions);
    if (sorted.some((a, i) => a.id !== form.actions[i].id))
      syncCaOrder(questionId, sorted);
  };
  const handleMoveCa = (questionId: string, actionId: string, dir: -1 | 1) => {
    const form = caFormState[questionId];
    if (!form) return;
    const moved = moveRow(form.actions, actionId, dir);
    if (moved === form.actions) return;
    setCaFormState((prev) => ({
      ...prev,
      [questionId]: { ...form, manualOrder: true },
    }));
    syncCaOrder(questionId, moved);
  };
  // Clone sits on the LAST row (Jim 10/30, D-15): copies that row's issue N
  // times with the location left blank — locations differ per lot (Jim
  // 2026-04-16), and each clone needs one before the form can be saved.
  const handleCloneLast = (questionId: string) => {
    const form = caFormState[questionId];
    const lastCa = form?.actions[form.actions.length - 1];
    if (!form || !lastCa) return;
    const countEl = document.getElementById(
      `clone-count-${questionId}`
    ) as HTMLInputElement | null;
    const count = Math.min(
      Math.max(parseInt(countEl?.value || '1', 10) || 1, 1),
      20
    );
    const clones = Array.from({ length: count }, () => ({
      id: generateId(),
      location: '',
      notes: lastCa.notes,
    }));
    syncCaOrder(questionId, [...form.actions, ...clones]);
  };
  const handleRemoveCa = (questionId: string, actionId: string) => {
    if (!window.confirm('Delete this corrective action?')) {
      return;
    }
    setCaFormState((prev) => {
      const f = prev[questionId];
      if (!f) return prev;
      return {
        ...prev,
        [questionId]: {
          ...f,
          actions: f.actions.filter((a) => a.id !== actionId),
        },
      };
    });
    setResponses((prev) => {
      const e = prev[questionId];
      if (!e) return prev;
      return {
        ...prev,
        [questionId]: {
          ...e,
          correctiveActions: (e.correctiveActions || []).filter(
            (a) => a.id !== actionId
          ),
        },
      };
    });
  };

  // Enh 3: dismiss the CA entry form without committing. Un-answers the
  // question when there's nothing committed, so the orphan detector doesn't
  // immediately reopen.
  const handleCloseCaForm = (questionId: string) => {
    const form = caFormState[questionId];
    const hasUnsaved = !!form && (form.notes.trim() || form.location.trim());
    if (hasUnsaved && !window.confirm('Discard unsaved corrective action?')) {
      return;
    }
    setSubmitError(null);
    setCaFormState((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setExpandedCaForms((prev) => {
      const n = new Set(prev);
      n.delete(questionId);
      return n;
    });
    const resp = responses[questionId];
    const committed = resp?.correctiveActions?.length ?? 0;
    if (resp?.value === false && committed === 0) {
      // The inspector answered No, the CA box opened, and they trashed it
      // without adding anything. Per Jim (2026-06), that means "I hit the wrong
      // button — I meant Yes," so flip the answer to Yes rather than leaving it
      // blank (the old behavior). value=true also keeps the orphan detector
      // from immediately reopening the form. Guarded: if unresolved CAs from
      // prior inspections still require a No, we can't go Yes — leave it
      // un-answered instead.
      const openActions = openCorrectiveActions[questionId] ?? [];
      const unverifiedTableCAs = (
        correctiveActionsData?.corrective_actions ?? []
      ).filter(
        (ca) =>
          ca.question_id === questionId &&
          ca.status === 'open' &&
          !verifiedCaIds[ca.id]
      );
      if (openActions.length === 0 && unverifiedTableCAs.length === 0) {
        setResponses((prev) => ({
          ...prev,
          [questionId]: {
            questionId,
            value: true,
            requiresCorrection: false,
            timestamp: new Date().toISOString(),
          },
        }));
      } else {
        setResponses((prev) => {
          const next = { ...prev };
          delete next[questionId];
          return next;
        });
      }
    }
  };

  const handleSaveCaForm = (questionId: string) => {
    const form = caFormState[questionId];
    if (!form) return;
    setSubmitError(null);
    let all = [
      ...(form.actions.length
        ? form.actions
        : (responses[questionId]?.correctiveActions ?? [])),
    ];
    // Capture the open row even if only one field was filled — "Done" should
    // never discard text the inspector typed.
    const notes = form.notes.trim();
    const location = form.location.trim();
    if (notes || location)
      all = [...all, { id: generateId(), location, notes }];
    // JIM-1030-15: nothing is saved without a location.
    const unlocated = rowsWithoutLocation(all);
    if (unlocated.length > 0) {
      setSubmitError(
        `${unlocated.length} corrective action${unlocated.length === 1 ? ' still needs' : 's still need'} a location (lot or street) before the form can be saved.`
      );
      setCaFormState((prev) => ({
        ...prev,
        [questionId]: { ...form, notes: '', location: '', actions: all },
      }));
      return;
    }
    if (all.length > 0)
      setResponses((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          correctiveActions: all,
          notes: all[0]?.notes,
          location: all[0]?.location,
        },
      }));
    setCaFormState((prev) => ({
      ...prev,
      [questionId]: { notes: '', location: '', actions: all },
    }));
    setExpandedCaForms((prev) => {
      const n = new Set(prev);
      n.delete(questionId);
      return n;
    });
  };

  // Resolve one open finding and update local state. When the last CA for the
  // question clears (across all sources), auto-flip the response to Yes so
  // verifying becomes a complete action. The verified rows stay visible with
  // Undo; clicking No again reopens the entry form (see handleAnswer).
  const verifyOpenCa = async (questionId: string, findingId: string) => {
    try {
      await api.patch(`/sites/${siteId}/findings/${findingId}/resolve`, {
        resolution_notes: 'Verified during inspection',
      });
    } catch (e) {
      console.error('Failed to resolve finding:', e);
    }
    setOpenCorrectiveActions((prev) => {
      const remaining = (prev[questionId] || []).filter(
        (a) => a.findingId !== findingId
      );
      const u = { ...prev };
      if (remaining.length === 0) {
        delete u[questionId];
        const remainingUnverifiedTable = (
          correctiveActionsData?.corrective_actions ?? []
        ).filter(
          (c) =>
            c.question_id === questionId &&
            c.status === 'open' &&
            !verifiedCaIds[c.id]
        );
        const committed = responses[questionId]?.correctiveActions?.length ?? 0;
        if (remainingUnverifiedTable.length === 0 && committed === 0) {
          setResponses((p) => ({
            ...p,
            [questionId]: {
              questionId,
              value: true,
              requiresCorrection: false,
              timestamp: new Date().toISOString(),
            },
          }));
          setExpandedCaForms((p) => {
            const n = new Set(p);
            n.delete(questionId);
            return n;
          });
        }
      } else {
        u[questionId] = remaining;
      }
      return u;
    });
  };

  // Clear a stage date the inspector set by accident. Flips the cell back to
  // the "+ Today" affordance and persists null when there's a saved record.
  const handleClearStageDate = async (
    stageName: string,
    field: 'start' | 'completion'
  ) => {
    const existingId = stageDates[stageName]?.extractedStageId;
    setStageDates((prev) => ({
      ...prev,
      [stageName]: {
        start: prev[stageName]?.start || '',
        completion: prev[stageName]?.completion || '',
        extractedStageId: prev[stageName]?.extractedStageId,
        [field]: '',
      },
    }));
    if (existingId) {
      try {
        await api.patch(`/extracted-stages/${existingId}`, {
          [field === 'start' ? 'start_date_noted' : 'completion_date_noted']:
            null,
        });
      } catch (err) {
        console.error('Failed to clear stage date:', err);
      }
    }
  };

  // Auto-capture (Jim, 2026-06): a corrective action typed into the open row
  // but never "+ Add"-ed used to vanish on submit. We now sweep those drafts
  // into the report at submit time. Because the real submit reads the parent
  // `responses` state, we commit the drafts and defer onSubmit by one render
  // (via submitRequested) so the captured CAs are flushed first.
  const [submitRequested, setSubmitRequested] = useState(false);
  const stageSectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!submitRequested) return;
    setSubmitRequested(false);
    onSubmit();
  }, [submitRequested, onSubmit]);

  // JIM-1015-01: a final inspection with stages still open asks first; the inspector may go
  // ahead anyway (the site is not complete) — that choice holds until the box is unticked.
  const [finalWarning, setFinalWarning] = useState<string[] | null>(null);
  const [finalBypassed, setFinalBypassed] = useState(false);
  useEffect(() => {
    if (!isFinalInspection) {
      setFinalWarning(null);
      setFinalBypassed(false);
    }
  }, [isFinalInspection]);

  const guardedSubmit = (bypassFinal = finalBypassed) => {
    // 0. JIM-1006-14 (Jim 2026-10-06): the review does not run until every
    //    question has an answer; a tap before that says what is left and
    //    scrolls to the first one.
    const unanswered = inspectionCategories.flatMap((cat) =>
      cat.questions
        .filter((q) => isQuestionVisible(q, responses) && !(q.id in responses))
        .map((q) => q.id)
    );
    if (unanswered.length > 0) {
      setSubmitError(
        `${unanswered.length} question${unanswered.length === 1 ? '' : 's'} still to answer before the report can be reviewed — the first one is below.`
      );
      setTimeout(() => {
        questionRefs.current[unanswered[0]]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 0);
      return;
    }
    // 0.5 JIM-1015-01: final inspection with stages still open → warn, with a bypass
    if (isFinalInspection && !bypassFinal) {
      const open = stagesWithoutCompletion(allStages, stageDates);
      if (open.length > 0) {
        setFinalWarning(open);
        return;
      }
    }
    setFinalWarning(null);
    // 1-2. Sweep any CA typed into the open row but never "+ Add"-ed into the
    //      report (Jim, 2026-06). See correctiveActionCapture for the tested
    //      transform that keeps an already-committed CA and the unsaved row.
    const { captured, capturedIds: draftIds } = captureUnsavedCorrectiveActions(
      responses,
      caFormState,
      generateId
    );

    // 2.5 JIM-1030-15: every corrective action carries a location.
    const unlocated = Object.entries(captured)
      .filter(
        ([, r]) => rowsWithoutLocation(r.correctiveActions ?? []).length > 0
      )
      .map(([id]) => id);
    if (unlocated.length > 0) {
      setSubmitError(
        `${unlocated.length} question${unlocated.length === 1 ? ' has' : 's have'} a corrective action without a location (lot or street). Fill it in before the report can be reviewed.`
      );
      setExpandedCaForms((prev) => {
        const n = new Set(prev);
        for (const id of unlocated) n.add(id);
        return n;
      });
      setTimeout(() => {
        questionRefs.current[unlocated[0]]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 0);
      return;
    }
    // 3. Block only the ambiguous case (Chris, 2026-06): a "No" answer with no
    //    corrective action at all — none committed, none captured, none carried.
    const missing: string[] = [];
    for (const cat of inspectionCategories) {
      for (const q of cat.questions) {
        const resp = captured[q.id];
        if (resp?.value !== false) continue;
        // JIM-1008-06: a No that points at another question is covered by
        // that question's corrective action.
        if (resp.sameAsQuestionId) continue;
        const committed = resp.correctiveActions?.length || 0;
        const carriedOver = openCorrectiveActions[q.id]?.length || 0;
        if (committed === 0 && carriedOver === 0) missing.push(q.id);
      }
    }

    if (missing.length > 0) {
      setSubmitError(
        `${missing.length} "No" answer${missing.length === 1 ? ' needs' : 's need'} a corrective action. Add one, or change the answer to Yes.`
      );
      setExpandedCaForms((prev) => {
        const n = new Set(prev);
        for (const id of missing) n.add(id);
        return n;
      });
      setCaFormState((prev) => {
        const next = { ...prev };
        for (const id of missing) {
          if (!next[id]) next[id] = { notes: '', location: '', actions: [] };
        }
        return next;
      });
      const firstId = missing[0];
      setTimeout(() => {
        questionRefs.current[firstId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 0);
      return;
    }

    // 4. Commit captured drafts, clear the open rows, then submit once flushed.
    setSubmitError(null);
    if (draftIds.length > 0) {
      setResponses(captured);
      setCaFormState((prev) => {
        const next = { ...prev };
        for (const id of draftIds) {
          if (next[id]) {
            next[id] = {
              ...next[id],
              notes: '',
              location: '',
              actions: captured[id]?.correctiveActions || next[id].actions,
            };
          }
        }
        return next;
      });
      setSubmitRequested(true);
    } else {
      onSubmit();
    }
  };

  // Restore the swipe-up-to-open-site-detail gesture on mobile (#267). The
  // visible "Site Detail" pill stays the desktop affordance.
  const swipeHandlers = useSwipeUp(onShowSiteInfo);

  return (
    <div
      className={`min-h-screen bg-slate-50 dark:bg-slate-900 ${finalWarning ? 'pb-60' : 'pb-20'}`}
      {...swipeHandlers}
    >
      {/* Sticky header */}
      {/* Slim so it can stay pinned the whole inspection without eating the
          screen; site + client both visible in case the wrong site was
          opened (Jim 2026-08-30). */}
      {/* Pinned directly under the Lite app header (sticky z-40), whatever
          its height: 64px on a phone, 109px with the tab row on a wide
          screen. A fixed top-16 slid this bar underneath the taller header,
          so it vanished on scroll there (Jim 2026-08-31: "the project title
          and client is not fixed"; again 2026-10-05 on his desktop). */}
      <div
        data-testid="lite-site-ribbon"
        style={{ top: `var(${LITE_HEADER_HEIGHT_VAR}, 4rem)` }}
        className="sticky z-30 bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 text-white px-4 py-1.5"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0 leading-tight">
            <h1 className="text-sm font-semibold truncate">{siteName}</h1>
            {clientName && (
              <p className="text-[11px] text-white/80 truncate">{clientName}</p>
            )}
          </div>
          {onShowSiteInfo && (
            // Solid white pill so it stands out from the colored ribbon —
            // inspectors kept losing the old translucent bg-white/10 chip (#267).
            <button
              onClick={onShowSiteInfo}
              className="h-8 px-3 flex items-center gap-1.5 justify-center rounded-lg bg-white text-primary-700 hover:bg-primary-50 active:bg-primary-100 transition-colors text-xs font-bold whitespace-nowrap shadow-sm"
            >
              <Info className="w-3.5 h-3.5" />
              Site Detail
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Inspection date + final flag — one small line at the top (Jim
            2026-08-30); replaces the old Inspection Details card below. */}
        {/* wraps on a narrow phone: date + time are wider than 390px with the label, and a row
            that cannot wrap widened the whole page (the Review bar ran off the screen, 10/16) */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-xs">
          <label
            htmlFor="lite-conducted-date"
            className="font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap"
          >
            Inspection date
          </label>
          <InspectionDateField
            id="lite-conducted-date"
            value={conductedDate}
            onChange={setConductedDate}
          />
          <InspectionTimeField
            id="lite-conducted-time"
            value={conductedTime}
            onChange={setConductedTime}
          />
        </div>

        {/* ═══ SECTION 1: Construction Stages ═══ */}
        <div ref={stageSectionRef} />
        {!siteLoading && allStages.length > 0 && (
          <StageTable
            allStages={allStages}
            currentStage={currentStage}
            stageDates={stageDates}
            setStageDates={setStageDates}
            today={today}
            handleStageDateBlur={handleStageDateBlur}
            handleClearStageDate={handleClearStageDate}
            handleStageDateSetAndSave={handleStageDateSetAndSave}
          />
        )}
        {siteLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        )}

        {/* ═══ SECTION 3: Inspection Questions ═══ */}
        {inspectionCategories.map((category) => {
          const visibleQuestions = category.questions.filter((q) =>
            isQuestionVisible(q, responses)
          );
          if (visibleQuestions.length === 0) return null;
          const answered = visibleQuestions.filter(
            (q) => responses[q.id]
          ).length;
          const CategoryIcon = category.icon;

          return (
            <div
              key={category.name}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <div className="w-7 h-7 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <CategoryIcon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                </div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white flex-1">
                  {category.name}
                </h2>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {answered}/{visibleQuestions.length}
                </span>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {visibleQuestions.map((question) => {
                  const response = responses[question.id];
                  const lastAns = lastAnswers?.[question.id];
                  const openCas = openCorrectiveActions[question.id] || [];
                  const isExpanded = expandedCaForms.has(question.id);
                  const form = caFormState[question.id];
                  const tableCAs = (
                    correctiveActionsData?.corrective_actions ?? []
                  ).filter(
                    (ca) =>
                      ca.question_id === question.id &&
                      ca.status === 'open' &&
                      !verifiedCaIds[ca.id]
                  );
                  const verifiedTableCAs = (
                    correctiveActionsData?.corrective_actions ?? []
                  ).filter(
                    (ca) =>
                      ca.question_id === question.id &&
                      ca.status === 'open' &&
                      verifiedCaIds[ca.id]
                  );
                  const isYes = response?.value === true;
                  const isNo = response?.value === false;
                  const isNa = response?.value === 'na';
                  const isNaType = question.type === 'yes-no-na';

                  return (
                    <div
                      key={question.id}
                      ref={(el) => {
                        questionRefs.current[question.id] = el;
                      }}
                      className={`px-4 py-3 transition-colors ${isNo ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 dark:text-slate-300 leading-snug">
                            {question.question}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {question.isHighPriority && (
                              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                                HIGH PRIORITY
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleAnswer(question.id, true)}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all flex items-center gap-1 ${
                              isYes
                                ? 'bg-green-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => handleAnswer(question.id, false)}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${isNo ? 'bg-red-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20'}`}
                          >
                            No
                          </button>
                          {isNaType && (
                            <button
                              onClick={() => handleAnswer(question.id, 'na')}
                              className={`px-2 py-1 text-xs font-semibold rounded-md transition-all ${isNa ? 'bg-primary-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'}`}
                            >
                              N/A
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Previous inspection findings (historical CA details) */}
                      {lastAns?.findings &&
                        lastAns.findings.length > 0 &&
                        !response && (
                          <div className="mt-1.5 space-y-1">
                            {lastAns.findings.map((f, fi) => (
                              <div
                                key={fi}
                                className={`flex items-start gap-2 text-xs px-2.5 py-1.5 rounded-lg ${f.is_resolved ? 'bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'}`}
                              >
                                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <span className="font-medium">
                                    {f.is_resolved ? 'Resolved' : 'Prior CA'}:
                                  </span>{' '}
                                  <span className="truncate">
                                    {f.description}
                                  </span>
                                  {f.location && (
                                    <span className="text-[10px] ml-1 opacity-70">
                                      ({f.location})
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                      {openCas.length > 0 &&
                        !isYes &&
                        tableCAs.length === 0 &&
                        verifiedTableCAs.length === 0 &&
                        (() => {
                          // Group by description so duplicate clones collapse into
                          // a single "Multiple corrective action items (N)" row.
                          // verifiedTableCAs gate suppresses this path once the
                          // /corrective-actions list has been fully verified —
                          // the findings-derived openCAs are redundant clones
                          // that don't get cleared by tableCA verifies.
                          const groups: {
                            key: string;
                            notes: string;
                            items: OpenCA[];
                          }[] = [];
                          for (const oa of openCas) {
                            const g = groups.find((x) => x.notes === oa.notes);
                            if (g) g.items.push(oa);
                            else
                              groups.push({
                                key: oa.findingId,
                                notes: oa.notes,
                                items: [oa],
                              });
                          }
                          return (
                            <div className="mt-2 space-y-1">
                              {groups.map((g) => {
                                if (g.items.length === 1) {
                                  const oa = g.items[0];
                                  return (
                                    <div
                                      key={oa.findingId}
                                      className="flex items-center gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg"
                                    >
                                      <AlertTriangle className="w-3 h-3 shrink-0 text-amber-500" />
                                      <span className="flex-1 truncate text-amber-700 dark:text-amber-400">
                                        Open CA:{' '}
                                        {oa.location ? (
                                          <>
                                            <b>{oa.location}:</b> {oa.notes}
                                          </>
                                        ) : (
                                          oa.notes
                                        )}
                                      </span>
                                      <button
                                        onClick={() =>
                                          verifyOpenCa(
                                            question.id,
                                            oa.findingId
                                          )
                                        }
                                        className="shrink-0 px-2 py-0.5 text-[10px] font-semibold bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                      >
                                        Verify
                                      </button>
                                    </div>
                                  );
                                }
                                const groupKey = `${question.id}:${g.key}`;
                                const isGroupOpen =
                                  expandedCaGroups.has(groupKey);
                                return (
                                  <div key={groupKey} className="space-y-1">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedCaGroups((prev) => {
                                          const n = new Set(prev);
                                          if (n.has(groupKey))
                                            n.delete(groupKey);
                                          else n.add(groupKey);
                                          return n;
                                        })
                                      }
                                      className="w-full flex items-center gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 px-2.5 py-1.5 rounded-lg text-left transition-colors"
                                    >
                                      {isGroupOpen ? (
                                        <ChevronDown className="w-3 h-3 shrink-0 text-amber-500" />
                                      ) : (
                                        <ChevronRight className="w-3 h-3 shrink-0 text-amber-500" />
                                      )}
                                      <span className="flex-1 truncate text-amber-700 dark:text-amber-400">
                                        <b>
                                          Multiple corrective action items (
                                          {g.items.length})
                                        </b>
                                        : {g.notes}
                                      </span>
                                    </button>
                                    {isGroupOpen && (
                                      <div className="ml-5 space-y-1">
                                        {g.items.map((oa) => (
                                          <div
                                            key={oa.findingId}
                                            className="flex items-center gap-2 text-xs bg-amber-50/70 dark:bg-amber-900/10 px-2.5 py-1.5 rounded-lg"
                                          >
                                            <AlertTriangle className="w-3 h-3 shrink-0 text-amber-500" />
                                            <span className="flex-1 truncate text-amber-700 dark:text-amber-400">
                                              {oa.location ? (
                                                <>
                                                  <b>{oa.location}:</b>{' '}
                                                  {oa.notes}
                                                </>
                                              ) : (
                                                oa.notes
                                              )}
                                            </span>
                                            <button
                                              onClick={() =>
                                                verifyOpenCa(
                                                  question.id,
                                                  oa.findingId
                                                )
                                              }
                                              className="shrink-0 px-2 py-0.5 text-[10px] font-semibold bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                            >
                                              Verify
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            for (const oa of g.items) {
                                              await verifyOpenCa(
                                                question.id,
                                                oa.findingId
                                              );
                                            }
                                          }}
                                          className="text-[10px] font-semibold text-green-700 dark:text-green-400 hover:underline"
                                        >
                                          Verify all {g.items.length}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      {tableCAs.length > 0 &&
                        !isYes &&
                        tableCAs.map((ca) => (
                          <div
                            key={ca.id}
                            className="mt-1 flex items-center gap-2 text-xs bg-amber-50 dark:bg-amber-900/10 px-2.5 py-1 rounded"
                          >
                            <span className="flex-1 truncate text-amber-600 dark:text-amber-400">
                              {ca.location ? (
                                <>
                                  <b>{ca.location}:</b>{' '}
                                  {ca.notes || ca.question_text}
                                </>
                              ) : (
                                ca.notes || ca.question_text
                              )}
                            </span>
                            <button
                              onClick={() => {
                                // Mark verified locally only. Resolution is
                                // DEFERRED to inspection submit (LiteInspection-
                                // FlowPage resolves verifiedCaIds after the
                                // inspection POST). This keeps Verify reversible
                                // and refresh-safe — verifiedCaIds round-trips
                                // through the IndexedDB draft, so a mid-review
                                // refresh no longer loses CAs (Jim, 2026-06).
                                setVerifiedCaIds((prev) => ({
                                  ...prev,
                                  [ca.id]: 'Verified during inspection',
                                }));
                                const remainingUnverifiedTable = (
                                  correctiveActionsData?.corrective_actions ??
                                  []
                                ).filter(
                                  (c) =>
                                    c.question_id === question.id &&
                                    c.status === 'open' &&
                                    !verifiedCaIds[c.id] &&
                                    c.id !== ca.id
                                );
                                const committed =
                                  responses[question.id]?.correctiveActions
                                    ?.length ?? 0;
                                // If every open CA the table shows is verified
                                // and no new CAs have been committed, the
                                // question is satisfied — flip No → Yes.
                                if (
                                  remainingUnverifiedTable.length === 0 &&
                                  committed === 0
                                ) {
                                  setResponses((prev) => {
                                    // Keep the answer Verify replaces, so Undo
                                    // can put it back (Omar 10/29: Undo cleared
                                    // the mark but the auto-Yes stayed, and the
                                    // Yes resolved the item at submit).
                                    preVerifyAnswers.current[question.id] =
                                      prev[question.id];
                                    return {
                                      ...prev,
                                      [question.id]: {
                                        questionId: question.id,
                                        value: true,
                                        requiresCorrection: false,
                                        timestamp: new Date().toISOString(),
                                      },
                                    };
                                  });
                                  setExpandedCaForms((prev) => {
                                    const n = new Set(prev);
                                    n.delete(question.id);
                                    return n;
                                  });
                                }
                              }}
                              className="shrink-0 px-2 py-0.5 text-[10px] font-semibold bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                            >
                              Verify
                            </button>
                          </div>
                        ))}
                      {verifiedTableCAs.length > 0 &&
                        verifiedTableCAs.map((ca) => (
                          <div
                            key={ca.id}
                            className="mt-1 flex items-center gap-2 text-xs bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 px-2.5 py-1 rounded"
                          >
                            <CheckCircle2 className="w-3 h-3 shrink-0 text-yellow-600 dark:text-yellow-400" />
                            <span className="flex-1 truncate text-yellow-800 dark:text-yellow-300">
                              {ca.location ? (
                                <>
                                  <b>{ca.location}:</b>{' '}
                                  {ca.notes || ca.question_text}
                                </>
                              ) : (
                                ca.notes || ca.question_text
                              )}
                            </span>
                            <span className="shrink-0 text-[10px] font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wide">
                              Verified
                            </span>
                            <button
                              onClick={() => {
                                // Resolution is deferred to submit, so the CA is
                                // still open server-side — undo is purely local:
                                // just clear the verified mark (no reopen call,
                                // which would error on an already-open CA).
                                setVerifiedCaIds((prev) => {
                                  const next = { ...prev };
                                  delete next[ca.id];
                                  return next;
                                });
                                // Verify flipped the question to Yes when this
                                // was the last open item; an open item again
                                // means the answer is No again.
                                const before =
                                  preVerifyAnswers.current[question.id];
                                if (
                                  before &&
                                  responses[question.id]?.value === true
                                ) {
                                  delete preVerifyAnswers.current[question.id];
                                  setResponses((prev) => ({
                                    ...prev,
                                    [question.id]: before,
                                  }));
                                }
                              }}
                              className="shrink-0 flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                            >
                              <Undo2 className="w-3 h-3" />
                              Undo
                            </button>
                          </div>
                        ))}
                      {isNo && response?.sameAsQuestionId && (
                        <div
                          className="mt-1.5 flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded"
                          data-testid="same-issue-chip"
                        >
                          <AlertCircle className="w-3 h-3 shrink-0" />
                          <span className="min-w-0 truncate">
                            Same issue as:{' '}
                            {allQuestionsFlat.find(
                              (q) => q.id === response.sameAsQuestionId
                            )?.question ?? 'another question'}
                          </span>
                          <button
                            type="button"
                            onClick={() => linkSameIssue(question.id, null)}
                            className="ml-auto shrink-0 text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            Change
                          </button>
                        </div>
                      )}
                      {!isExpanded &&
                        !response?.sameAsQuestionId &&
                        response?.correctiveActions &&
                        response.correctiveActions.length > 0 && (
                          <div className="mt-1.5 space-y-1">
                            {response.correctiveActions.map((ca) => (
                              <div
                                key={ca.id}
                                className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded"
                              >
                                <AlertCircle className="w-3 h-3 shrink-0" />
                                <span className="truncate">
                                  {ca.location}: {ca.notes}
                                </span>
                              </div>
                            ))}
                            <button
                              onClick={() =>
                                setExpandedCaForms(
                                  (p) => new Set([...p, question.id])
                                )
                              }
                              className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      {isExpanded && !response?.sameAsQuestionId && (
                        <div className="mt-2 bg-red-50 dark:bg-red-900/10 rounded-lg p-2.5 border border-red-200 dark:border-red-800">
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-xs font-semibold text-red-700 dark:text-red-400">
                              Corrective Action
                            </p>
                            <button
                              onClick={() => handleCloseCaForm(question.id)}
                              className="text-red-400 hover:text-red-600 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                              title="Close and discard"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {(() => {
                            // JIM-1008-06 (Shane): the same problem under two
                            // questions gets one corrective action.
                            const choices = sameIssueChoices(
                              responses,
                              allQuestionsFlat,
                              question.id
                            );
                            if (!choices.length) return null;
                            return (
                              <label className="mb-1.5 flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                                <span className="shrink-0">Same issue as</span>
                                <select
                                  aria-label={`Same issue as, for: ${question.question}`}
                                  value=""
                                  onChange={(e) =>
                                    e.target.value &&
                                    linkSameIssue(question.id, e.target.value)
                                  }
                                  className="min-w-0 flex-1 px-1.5 py-0.5 border rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600 text-[11px]"
                                >
                                  <option value="">
                                    another No on this visit…
                                  </option>
                                  {choices.map((q) => (
                                    <option key={q.id} value={q.id}>
                                      {q.question}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            );
                          })()}
                          {form?.actions && form.actions.length > 0 && (
                            <div className="mb-1.5 space-y-1">
                              {form.actions.map((ca, index) => (
                                <div
                                  key={ca.id}
                                  className="flex items-center gap-1.5 bg-white dark:bg-slate-800 rounded px-2 py-1 text-xs"
                                  data-testid="ca-row"
                                >
                                  <input
                                    type="text"
                                    placeholder="Location *"
                                    value={ca.location}
                                    onChange={(e) =>
                                      handleUpdateCaLocation(
                                        question.id,
                                        ca.id,
                                        e.target.value
                                      )
                                    }
                                    onBlur={() => handleResortCa(question.id)}
                                    className={`shrink-0 w-24 px-1.5 py-0.5 border rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-red-400 ${!ca.location ? 'border-red-400 dark:border-red-500' : 'border-slate-200 dark:border-slate-600'}`}
                                  />
                                  <span className="text-slate-400 shrink-0">
                                    :
                                  </span>
                                  <span className="text-slate-700 dark:text-slate-300 truncate flex-1">
                                    {ca.notes}
                                  </span>
                                  <button
                                    type="button"
                                    aria-label="Move up"
                                    onClick={() =>
                                      handleMoveCa(question.id, ca.id, -1)
                                    }
                                    disabled={index === 0}
                                    className="text-slate-400 hover:text-slate-600 disabled:opacity-30 shrink-0"
                                  >
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label="Move down"
                                    onClick={() =>
                                      handleMoveCa(question.id, ca.id, 1)
                                    }
                                    disabled={index === form.actions.length - 1}
                                    className="text-slate-400 hover:text-slate-600 disabled:opacity-30 shrink-0"
                                  >
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleRemoveCa(question.id, ca.id)
                                    }
                                    className="text-red-400 hover:text-red-600 shrink-0"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                  {index === form.actions.length - 1 && (
                                    <>
                                      <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        defaultValue="1"
                                        aria-label="Copies"
                                        id={`clone-count-${question.id}`}
                                        className="w-9 text-xs text-center px-1 py-0.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 shrink-0"
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleCloneLast(question.id)
                                        }
                                        className="flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 shrink-0"
                                      >
                                        <Copy className="w-3 h-3" />
                                        Clone
                                      </button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="space-y-1.5">
                            <input
                              type="text"
                              placeholder="Location *"
                              value={form?.location || ''}
                              onChange={(e) =>
                                setCaFormState((p) => ({
                                  ...p,
                                  [question.id]: {
                                    ...(p[question.id] || {
                                      notes: '',
                                      location: '',
                                      actions: [],
                                    }),
                                    location: e.target.value,
                                  },
                                }))
                              }
                              className="w-full text-xs px-2.5 py-1.5 border border-red-200 dark:border-red-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-400"
                            />
                            <textarea
                              placeholder="Describe the issue *"
                              value={form?.notes || ''}
                              onChange={(e) =>
                                setCaFormState((p) => ({
                                  ...p,
                                  [question.id]: {
                                    ...(p[question.id] || {
                                      notes: '',
                                      location: '',
                                      actions: [],
                                    }),
                                    notes: e.target.value,
                                  },
                                }))
                              }
                              rows={2}
                              className="w-full text-xs px-2.5 py-1.5 border border-red-200 dark:border-red-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-400 resize-none"
                            />
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <button
                              onClick={() => handleAddCa(question.id)}
                              // JIM-1015-10: the issue is required; JIM-1030-15: so is
                              // the location.
                              disabled={
                                !form?.notes?.trim() || !form?.location?.trim()
                              }
                              className="flex items-center gap-1 text-xs px-2.5 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                              Add
                            </button>
                            <button
                              onClick={() => handleSaveCaForm(question.id)}
                              className="text-xs px-2.5 py-1 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* EP-TRANSITION: legacy-carried corrective actions with no schema
            question. Verify closes them; Assign reparents to a numbered
            question (immediately pre-fills it as No — Jim 2026-08-04). New
            items can never be ADDED here; the bucket only drains. */}
        {looseCAs.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-amber-300 dark:border-amber-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-amber-100 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                Carried from legacy ({looseCAs.length})
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                Open corrective actions from the previous system. Verify if
                repaired, or assign each to the matching question above.
              </p>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {looseCAs.map((ca) => (
                <div key={ca.id} className="px-4 py-3">
                  <p className="text-sm text-slate-800 dark:text-slate-200">
                    {ca.notes || ca.question_text}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {ca.location && <span>📍 {ca.location}</span>}
                    {ca.deadline && <span>Noted {ca.deadline}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => void onVerifyLooseCa(ca.id)}
                      className="text-xs px-2.5 py-1.5 rounded-lg border border-success-300 dark:border-success-700 text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20"
                    >
                      Verified repaired
                    </button>
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value)
                          void onReparentLooseCa(ca.id, e.target.value);
                      }}
                      className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 max-w-[60%]"
                    >
                      <option value="" disabled>
                        Assign to question…
                      </option>
                      {(() => {
                        let n = 0;
                        return inspectionCategories.flatMap((cat) =>
                          cat.questions.map((q) => {
                            n += 1;
                            return (
                              <option key={q.id} value={q.id}>
                                {n}. {q.question.slice(0, 60)}
                              </option>
                            );
                          })
                        );
                      })()}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* F-Comment — universal Inspection Report Comment. Flows to page 1
            of the generated PDF under the "Comment" section. */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Inspection Comments
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Appears on page 1 of the PDF. Leave blank for "No comments". Page
              1 has room for {notesCapacity.lines} lines with {allStages.length}{' '}
              {allStages.length === 1 ? 'stage' : 'stages'}.
            </p>
          </div>
          <div className="px-4 py-3">
            <textarea
              value={reportComment}
              onChange={(e) =>
                setReportComment(
                  clampNoteToCapacity(e.target.value, notesCapacity)
                )
              }
              rows={notesRows}
              placeholder="Comments to appear on page 1 of the generated inspection report..."
              aria-describedby="inspection-notes-governor"
              className={`w-full text-sm px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none ${
                atNotesLimit
                  ? 'border-danger-400 dark:border-danger-600'
                  : 'border-slate-200 dark:border-slate-600'
              }`}
            />
            <p
              id="inspection-notes-governor"
              className={`mt-1 text-[10px] text-right ${
                atNotesLimit
                  ? 'text-danger-600 dark:text-danger-400 font-medium'
                  : 'text-slate-400 dark:text-slate-500'
              }`}
            >
              {governorLabel(reportComment, notesCapacity)}
              {atNotesLimit ? ' · page 1 is full' : ''}
            </p>
          </div>
        </div>

        {/* JIM-0919-09: City of Austin required details — the site's BMP
            log, edited here, printed on the report's last page. Austin
            sites only; notes above stay as they are. */}
        {austinDetails && (
          <AustinBmpDetails
            siteId={siteId}
            notes={austinNotes}
            setNotes={setAustinNotes}
            taps={austinTaps}
            setTaps={setAustinTaps}
            conditions={austinConditions}
            setConditions={setAustinConditions}
            filedInspectionId={austinFiledInspectionId}
            reportDate={conductedDate}
          />
        )}

        {/* JIM-0919-02: the two one-off toggles sit BELOW the notes (Jim
            2026-09-19 — "they're one-offs"). Wording leads with the action —
            sends the consultant a copy of the report with an attention
            banner, on or off the distribution list (Jim 2026-08-31). */}
        {setAlertConsultant && (
          <label className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 px-3 py-1.5 cursor-pointer text-xs">
            <input
              type="checkbox"
              checked={!!alertConsultant}
              onChange={(e) => setAlertConsultant(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-slate-800 dark:text-slate-200 truncate">
              <span className="font-semibold">Notify the Consultant</span> by
              sending a copy of the report — for anything like dated corrective
              actions or very poor conditions
            </span>
          </label>
        )}

        {/* #265 — one visit can satisfy both the weekly routine and a required
            rain event. Shown only for sites that require rain inspections.
            JIM-1005-01: begun from the Rain Event box, the visit already is
            the rain event; the box then asks whether it is the routine too. */}
        {rainEventStart && (
          <p
            className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/20 rounded-xl border border-sky-200 dark:border-sky-800 px-3 py-1.5 text-xs text-slate-800 dark:text-slate-200"
            data-testid="rain-event-visit"
          >
            <CloudRain className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
            <span>
              <span className="font-semibold">Rain event inspection</span> —
              filed as the rain event for this site
            </span>
          </p>
        )}
        {showRainEventToggle && setCoversRainEvent && (
          <label className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/20 rounded-xl border border-sky-200 dark:border-sky-800 px-3 py-1.5 cursor-pointer text-xs">
            <input
              type="checkbox"
              checked={!!coversRainEvent}
              onChange={(e) => setCoversRainEvent(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-slate-800 dark:text-slate-200 truncate">
              <span className="font-semibold">
                {rainEventStart
                  ? 'Also the weekly routine inspection'
                  : 'Also covers a required rain event'}
              </span>{' '}
              — counts for both, noted on the report
            </span>
          </label>
        )}

        {/* JIM-1005-11 (Jim 2026-10-05): the final-inspection question sits
            here with the other one-offs, not as a small box by the date, and
            says what it sets off. */}
        <label className="flex items-start gap-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 px-3 py-2 cursor-pointer text-xs">
          <input
            type="checkbox"
            checked={isFinalInspection}
            onChange={(e) => setIsFinalInspection(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-slate-800 dark:text-slate-200">
            <span className="font-semibold">Is this the final inspection?</span>{' '}
            — tells the consultant to confirm it with the client. Once
            confirmed, the site comes off your schedule; keep inspecting until
            then.
          </span>
        </label>
        {isFinalInspection && setFinalInspectionNote && (
          <label className="block bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-xs">
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              Why is it final?
            </span>{' '}
            <span className="text-slate-600 dark:text-slate-400">
              — the consultant reads this before confirming
            </span>
            <textarea
              value={finalInspectionNote}
              onChange={(e) => setFinalInspectionNote(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="e.g. Lot is stabilized, grass is in, builder signed off."
              data-testid="final-inspection-note"
              className="mt-1.5 w-full rounded-lg border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-slate-900 dark:text-white"
            />
          </label>
        )}
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-4 py-2.5">
        {finalWarning && (
          <div
            role="alert"
            className="mb-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 space-y-2"
          >
            <p>
              <span className="font-semibold">
                Final inspection, but the site is not complete.
              </span>{' '}
              {finalWarning.length === 1
                ? 'This stage has'
                : 'These stages have'}{' '}
              no completion date: {finalWarning.join(', ')}.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setFinalWarning(null);
                  stageSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold"
              >
                Add completion dates
              </button>
              <button
                type="button"
                onClick={() => {
                  setFinalBypassed(true);
                  guardedSubmit(true);
                }}
                className="px-3 py-1.5 rounded-lg border border-amber-400 dark:border-amber-600 font-semibold"
              >
                Final anyway — site not complete
              </button>
              <button
                type="button"
                onClick={() => setIsFinalInspection(false)}
                className="px-3 py-1.5 rounded-lg font-semibold underline"
              >
                Not the final inspection
              </button>
            </div>
          </div>
        )}
        {(submitError || serverError) && (
          <div className="mb-2 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{submitError || serverError}</span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <span className="flex-1 text-xs text-slate-500 dark:text-slate-400">
            {answeredQuestions === totalQuestions ? (
              <span className="text-green-600 dark:text-green-400 font-medium">
                All answered
              </span>
            ) : (
              <>{totalQuestions - answeredQuestions} unanswered</>
            )}
          </span>
          <button
            onClick={() => guardedSubmit()}
            aria-disabled={answeredQuestions < totalQuestions}
            className={`px-5 py-2 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-1.5 ${
              answeredQuestions < totalQuestions
                ? 'bg-primary-600/50 hover:bg-primary-600/60'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            <FileText className="w-4 h-4" />
            Review Report
          </button>
        </div>
      </div>
    </div>
  );
}
